import { useMemo } from 'react'

import { useModals, useNumericParams, useSetState } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import CreateOrEditAdminTeamMemberModal from '@modals/CreateOrEditAdminTeamMemberModal'

import type { TeamMemberModel } from '@models/types'

import PageContent from './components/PageContent'

const defaultState = {
  showCreateOrEditAdminTeamMemberModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

/* eslint-disable no-undef */
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    CreateOrEditAdminTeamMemberModal: {
      closeModal: () => setState({ showCreateOrEditAdminTeamMemberModal: false }),
      createTeamMember: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateTeamMember: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const Details = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showCreateOrEditAdminTeamMemberModal,
  } = state

  const { teamId } = useNumericParams()

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      showCreateOrEditAdminTeamMemberModal: (payload: { teamMember: Partial<TeamMemberModel> }) => {
        setModalData('CreateOrEditAdminTeamMemberModal', payload)
        setState({ showCreateOrEditAdminTeamMemberModal: true })
      },
      updateTeam: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent teamId={teamId} />

        <CreateOrEditAdminTeamMemberModal
          callbacks={callbacks('CreateOrEditAdminTeamMemberModal', setState)}
          showModal={showCreateOrEditAdminTeamMemberModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Details
